import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { GoalPriority, GoalRow, GoalStatus } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';

/**
 * Goals / dreams: long-horizon home improvements the user is dreaming
 * about (new deck, kitchen reno, paid-off solar). Distinct from tasks —
 * those are recurring upkeep on a calendar. Goals carry est cost and a
 * target date so the user can use them for budgeting + motivation.
 */

export const GOAL_CATEGORIES = [
  'Kitchen',
  'Bathroom',
  'Bedroom',
  'Living space',
  'Basement',
  'Garage',
  'Exterior',
  'Landscaping',
  'Energy',
  'Tech / smart home',
  'Safety',
  'Furniture',
  'Big repair',
  'Other',
] as const;

export const GOAL_STATUS_OPTIONS: { v: GoalStatus; label: string; sub: string }[] = [
  { v: 'someday', label: 'Someday', sub: 'A dream, no timeline yet' },
  { v: 'planning', label: 'Planning', sub: 'Researching contractors / prices' },
  { v: 'in-progress', label: 'In progress', sub: 'Work has started' },
  { v: 'done', label: 'Done', sub: 'Achieved — keep for history' },
];

export const GOAL_PRIORITY_OPTIONS: { v: GoalPriority; label: string }[] = [
  { v: 1, label: 'High' },
  { v: 2, label: 'Medium' },
  { v: 3, label: 'Low' },
];

export interface NewGoalInput {
  propertyId: string;
  title: string;
  why?: string | null;
  category?: string | null;
  target_date?: number | null;
  est_cost_cents?: number | null;
  status?: GoalStatus;
  priority?: GoalPriority;
  notes?: string | null;
  photo_uri?: string | null;
  extra_photos?: string[] | null;
  linked_task_ids?: string[] | null;
}

export function parseExtraPhotos(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

export function parseLinkedTaskIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

export async function addGoal(db: SQLiteDatabase, input: NewGoalInput): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO goal (
      id, property_id, title, why, category, target_date, est_cost_cents,
      status, priority, notes, photo_uri, extra_photos_json, linked_task_ids_json,
      created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.title.trim(),
    input.why ?? null,
    input.category ?? null,
    input.target_date ?? null,
    input.est_cost_cents ?? null,
    input.status ?? 'someday',
    input.priority ?? 2,
    input.notes ?? null,
    input.photo_uri ?? null,
    input.extra_photos?.length ? JSON.stringify(input.extra_photos) : null,
    input.linked_task_ids?.length ? JSON.stringify(input.linked_task_ids) : null,
    t,
    t
  );
  bumpDataRevision();
  return id;
}

export interface UpdateGoalInput extends Partial<Omit<NewGoalInput, 'propertyId'>> {}

export async function updateGoal(
  db: SQLiteDatabase,
  id: string,
  input: UpdateGoalInput
): Promise<void> {
  const t = now();
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  const fields: (keyof UpdateGoalInput)[] = [
    'title', 'why', 'category', 'target_date', 'est_cost_cents',
    'status', 'priority', 'notes', 'photo_uri',
  ];
  for (const f of fields) {
    if (input[f] !== undefined) {
      sets.push(`${f} = ?`);
      args.push((input[f] as string | number | null) ?? null);
    }
  }
  if (input.extra_photos !== undefined) {
    sets.push(`extra_photos_json = ?`);
    args.push(input.extra_photos?.length ? JSON.stringify(input.extra_photos) : null);
  }
  if (input.linked_task_ids !== undefined) {
    sets.push(`linked_task_ids_json = ?`);
    args.push(input.linked_task_ids?.length ? JSON.stringify(input.linked_task_ids) : null);
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(t);
  args.push(id);
  await db.runAsync(`UPDATE goal SET ${sets.join(', ')} WHERE id = ?`, ...args);
  bumpDataRevision();
}

export async function deleteGoal(db: SQLiteDatabase, id: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE goal SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  bumpDataRevision();
}

export async function getGoalById(
  db: SQLiteDatabase,
  id: string
): Promise<GoalRow | null> {
  return await db.getFirstAsync<GoalRow>(
    `SELECT * FROM goal WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export interface GoalWithStats extends GoalRow {
  steps_total: number;
  steps_done: number;
}

/**
 * Same as listGoals but enriches each goal with completed/total counts for
 * its linked tasks. Single round-trip — collects all linked ids across the
 * property, asks the task table once, then re-distributes counts client-
 * side. Used by the goals list to render progress badges.
 */
export async function listGoalsWithStats(
  db: SQLiteDatabase,
  propertyId: string
): Promise<GoalWithStats[]> {
  const goals = await listGoals(db, propertyId);
  if (goals.length === 0) return [];
  const idsPerGoal = goals.map((g) => parseLinkedTaskIds(g.linked_task_ids_json));
  const allIds = Array.from(new Set(idsPerGoal.flat()));
  if (allIds.length === 0) {
    return goals.map((g) => ({ ...g, steps_total: 0, steps_done: 0 }));
  }
  const placeholders = allIds.map(() => '?').join(',');
  const rows = await db.getAllAsync<{ id: string; completed_at: number | null }>(
    `SELECT id, completed_at FROM task WHERE id IN (${placeholders}) AND deleted_at IS NULL`,
    ...allIds
  );
  const doneSet = new Set(rows.filter((r) => r.completed_at != null).map((r) => r.id));
  const existSet = new Set(rows.map((r) => r.id));
  return goals.map((g, i) => {
    const ids = idsPerGoal[i].filter((id) => existSet.has(id));
    return {
      ...g,
      steps_total: ids.length,
      steps_done: ids.filter((id) => doneSet.has(id)).length,
    };
  });
}

export async function listGoals(
  db: SQLiteDatabase,
  propertyId: string
): Promise<GoalRow[]> {
  return await db.getAllAsync<GoalRow>(
    `SELECT * FROM goal
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY
       CASE status
         WHEN 'in-progress' THEN 0
         WHEN 'planning' THEN 1
         WHEN 'someday' THEN 2
         WHEN 'done' THEN 3
       END,
       priority ASC,
       COALESCE(target_date, 99999999999999) ASC,
       created_at DESC`,
    propertyId
  );
}

export interface GoalTotals {
  count: number;
  someday: number;
  planning: number;
  inProgress: number;
  done: number;
  totalCostCents: number;
}

export async function getGoalTotals(
  db: SQLiteDatabase,
  propertyId: string
): Promise<GoalTotals> {
  const rows = await listGoals(db, propertyId);
  const totals: GoalTotals = {
    count: rows.length,
    someday: 0,
    planning: 0,
    inProgress: 0,
    done: 0,
    totalCostCents: 0,
  };
  for (const r of rows) {
    if (r.status === 'someday') totals.someday += 1;
    else if (r.status === 'planning') totals.planning += 1;
    else if (r.status === 'in-progress') totals.inProgress += 1;
    else if (r.status === 'done') totals.done += 1;
    if (r.est_cost_cents && r.status !== 'done') totals.totalCostCents += r.est_cost_cents;
  }
  return totals;
}
